import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface MenuBarTourStep {
  id: string;
  title: string;
  description: string;
}

interface MenuBarTourProps {
  steps: MenuBarTourStep[];
  onComplete?: () => void | Promise<void>;
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

const CARD_WIDTH = 320;
const CARD_MIN_HEIGHT = 194;
const VIEWPORT_PADDING = 16;
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

let hasOpenedMenuTourInCurrentPageLoad = false;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getDesktopTourTarget(stepId: string): Element | null {
  if (typeof document === 'undefined') return null;

  const selector = `[data-menu-tour-id="${stepId}"][data-menu-tour-surface="desktop"]`;
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(selector));

  const targetLink = candidates.find((element) => {
    const rect = element.getBoundingClientRect();
    const style = window.getComputedStyle(element);

    return (
      rect.width > 0 &&
      rect.height > 0 &&
      style.display !== 'none' &&
      style.visibility !== 'hidden'
    );
  }) ?? null;

  if (!targetLink) return null;

  const icon = targetLink.querySelector('img, svg');
  if (!icon) return targetLink;

  const iconRect = icon.getBoundingClientRect();
  const iconStyle = window.getComputedStyle(icon);

  if (
    iconRect.width > 0 &&
    iconRect.height > 0 &&
    iconStyle.display !== 'none' &&
    iconStyle.visibility !== 'hidden'
  ) {
    return icon;
  }

  return targetLink;
}

function rectFromElement(element: Element): TargetRect {
  const rect = element.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.right,
    bottom: rect.bottom,
  };
}

export const MenuBarTour: React.FC<MenuBarTourProps> = ({
  steps,
  onComplete,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [targetRect, setTargetRect] = React.useState<TargetRect | null>(null);
  const [isMounted, setIsMounted] = React.useState(false);
  const hasCompletedRef = React.useRef(false);

  const activeStep = steps[activeIndex];
  const isLastStep = activeIndex >= steps.length - 1;

  const completeTour = React.useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;
    setIsOpen(false);
    void onComplete?.();
  }, [onComplete]);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  React.useEffect(() => {
    if (typeof window === 'undefined' || steps.length === 0) return;
    if (hasOpenedMenuTourInCurrentPageLoad) return;

    const startTour = () => {
      if (hasOpenedMenuTourInCurrentPageLoad) return;
      if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return;
      if (!getDesktopTourTarget(steps[0].id)) return;
      hasOpenedMenuTourInCurrentPageLoad = true;
      setActiveIndex(0);
      setIsOpen(true);
    };

    const timeoutId = window.setTimeout(startTour, 700);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [steps]);

  React.useEffect(() => {
    if (!isOpen || !activeStep) {
      setTargetRect(null);
      return;
    }

    let animationFrameId = 0;
    let intervalId = 0;

    const updateTarget = () => {
      const target = getDesktopTourTarget(activeStep.id);
      if (!target) {
        setTargetRect(null);
        return;
      }

      target.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
      setTargetRect(rectFromElement(target));
    };

    const scheduleUpdate = () => {
      window.cancelAnimationFrame(animationFrameId);
      animationFrameId = window.requestAnimationFrame(updateTarget);
    };

    scheduleUpdate();
    intervalId = window.setInterval(scheduleUpdate, 250);
    window.addEventListener('resize', scheduleUpdate);
    window.addEventListener('scroll', scheduleUpdate, true);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearInterval(intervalId);
      window.removeEventListener('resize', scheduleUpdate);
      window.removeEventListener('scroll', scheduleUpdate, true);
    };
  }, [activeStep, isOpen]);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        completeTour();
      }

      if (event.key === 'ArrowRight') {
        setActiveIndex((current) => Math.min(current + 1, steps.length - 1));
      }

      if (event.key === 'ArrowLeft') {
        setActiveIndex((current) => Math.max(current - 1, 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [completeTour, isOpen, steps.length]);

  const goNext = () => {
    if (isLastStep) {
      completeTour();
      return;
    }

    setActiveIndex((current) => Math.min(current + 1, steps.length - 1));
  };

  const goBack = () => {
    setActiveIndex((current) => Math.max(current - 1, 0));
  };

  if (!isMounted || !isOpen || !activeStep || !targetRect) {
    return null;
  }

  const highlightPadding = 14;
  const spotlightTop = targetRect.top - highlightPadding;
  const spotlightLeft = targetRect.left - highlightPadding;
  const spotlightWidth = targetRect.width + highlightPadding * 2;
  const spotlightHeight = targetRect.height + highlightPadding * 2;
  const highlightStyle: React.CSSProperties = {
    top: spotlightTop,
    left: spotlightLeft,
    width: spotlightWidth,
    height: spotlightHeight,
  };

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const cardCanFitRight = targetRect.right + 18 + CARD_WIDTH <= viewportWidth - VIEWPORT_PADDING;
  const cardLeft = cardCanFitRight
    ? targetRect.right + 18
    : clamp(targetRect.left, VIEWPORT_PADDING, viewportWidth - CARD_WIDTH - VIEWPORT_PADDING);
  const cardTop = clamp(
    targetRect.top + targetRect.height / 2 - CARD_MIN_HEIGHT / 2,
    VIEWPORT_PADDING,
    viewportHeight - CARD_MIN_HEIGHT - VIEWPORT_PADDING
  );

  const connectorStyle: React.CSSProperties = {
    top: spotlightTop + spotlightHeight / 2,
    left: spotlightLeft + spotlightWidth + 6,
    width: Math.max(8, cardLeft - spotlightLeft - spotlightWidth - 12),
  };

  return createPortal(
    <div aria-live="polite" aria-label="Menu bar tutorial" className="pointer-events-none fixed inset-0 z-[80]">
      <div
        className="menu-tour-highlight pointer-events-none fixed rounded-[18px] border-2 border-[#20AEDC] transition-[top,left,width,height] duration-300 ease-out"
        style={{
          ...highlightStyle,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.3)',
        }}
      />
      {cardCanFitRight ? (
        <div
          className="menu-tour-connector pointer-events-none fixed h-px bg-[#20AEDC]/80 transition-[top,left,width] duration-300 ease-out"
          style={connectorStyle}
        />
      ) : null}
      <section
        className="pointer-events-auto fixed z-[82] w-[320px] rounded-lg border border-slate-200 bg-white p-4 shadow-[0_22px_70px_rgba(15,23,42,0.22)] transition-[top,left] duration-300 ease-out"
        style={{ top: cardTop, left: cardLeft }}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase leading-4 tracking-[0.12em] text-[#20AEDC]">
              Menu tour
            </p>
            <h2 className="mt-1 text-base font-semibold leading-6 text-slate-950">
              {activeStep.title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Skip menu tour"
            onClick={completeTour}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="mt-3 text-sm leading-5 text-slate-600">
          {activeStep.description}
        </p>

        <div className="mt-4 flex items-center gap-1.5" aria-hidden="true">
          {steps.map((step, index) => (
            <span
              key={step.id}
              className={cn(
                'h-1.5 rounded-full transition-all duration-200',
                index === activeIndex ? 'w-6 bg-[#20AEDC]' : 'w-1.5 bg-slate-300'
              )}
            />
          ))}
        </div>

        <div className="mt-5 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={completeTour}
            className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900"
          >
            Skip
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goBack}
              disabled={activeIndex === 0}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-slate-200 px-3 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Previous tutorial step"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              Back
            </button>
            <button
              type="button"
              onClick={goNext}
              className="inline-flex h-9 items-center justify-center gap-2 rounded-lg bg-[#20AEDC] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2288A8]"
            >
              {isLastStep ? 'Done' : 'Next'}
              {!isLastStep ? <ChevronRight className="h-4 w-4" aria-hidden="true" /> : null}
            </button>
          </div>
        </div>
      </section>
    </div>,
    document.body
  );
};
